import { PropsWithChildren } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useAuth } from '@workos-inc/authkit-react';

import { Loader } from '@/components/atoms';
import { RoutesEnum } from '@/enums';
import { useGetOrganizationByIdQuery } from '@/store/api/organization';
import { buildReturnPathAfterAuth } from '@/utils/authReturnPath';
import { captureAsyncError } from '@/utils/captureAsyncError';

function httpStatusFromQueryError(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null || !('status' in error)) {
    return undefined;
  }
  const status = (error as FetchBaseQueryError).status;
  return typeof status === 'number' ? status : undefined;
}

export const AuthWrapper = ({ children }: PropsWithChildren<unknown>) => {
  const { user, isLoading, organizationId, signIn } = useAuth();
  const location = useLocation();

  const pathsAllowingNoOrg: readonly string[] = [RoutesEnum.LOGIN, RoutesEnum.ONBOARDING];
  const allowWithoutOrganization =
    pathsAllowingNoOrg.includes(location.pathname) ||
    location.pathname.startsWith(`${RoutesEnum.ONBOARDING}/`);

  const {
    data: orgFromApi,
    isLoading: isOrgLookupLoading,
    isFetching: isOrgLookupFetching,
    isSuccess: isOrgLookupSuccess,
    isError: isOrgLookupError,
    error: orgLookupError,
  } = useGetOrganizationByIdQuery(undefined, {
    skip: Boolean(organizationId) || Boolean(!user) || allowWithoutOrganization,
  });

  if (isLoading) {
    return <Loader />;
  }

  if (!user) {
    const returnTo = buildReturnPathAfterAuth(location.pathname, location.search);
    void Promise.resolve(signIn({ state: { returnTo } })).catch(captureAsyncError);
    return null;
  }

  if (!organizationId && !allowWithoutOrganization) {
    if (isOrgLookupLoading || isOrgLookupFetching) {
      return <Loader />;
    }

    if (orgFromApi?.orgId) {
      return <Loader />;
    }

    const errStatus = isOrgLookupError ? httpStatusFromQueryError(orgLookupError) : undefined;
    if (isOrgLookupError && errStatus !== undefined && errStatus !== 404) {
      return <Loader />;
    }

    if (isOrgLookupSuccess || errStatus === 404) {
      return <Navigate to={RoutesEnum.ONBOARDING} replace />;
    }

    return <Loader />;
  }

  return <>{children}</>;
};
